import React from 'react'
import styled from 'styled-components';
import TimeLine from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { education, certifications } from '../../data/constants';
import EducationCard from '../Card/educationCard';
import { useLanguage } from '../../utils/LanguageContext';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    z-index: 1;
    align-items: center;
    padding: 40px 0px 80px 0px;
    @media (max-width: 960px) {
        padding: 20px 0px;
    }
`;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 1350px;
    padding: 80px 0;
    gap: 12px;
    @media (max-width: 960px) {
        padding: 40px 0;
    }
`;

const Title = styled.div`
    font-size: 42px;
    text-align: center;
    font-weight: 600;
    margin-top: 20px;
    color: ${({ theme }) => theme.text_primary};
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 32px;
    }
`;

const Desc = styled.div`
    font-size: 18px;
    text-align: center;
    max-width: 600px;
    color: ${({ theme }) => theme.text_secondary};
    padding: 0 16px;
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 16px;
    }
`;

const TimeLineSection = styled.div`
    width: 100%;
    max-width: 1000px;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
`;

const CertSection = styled.div`
    width: 100%;
    max-width: 1100px;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const CertTitle = styled.h3`
    font-size: 32px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
    margin-bottom: 12px;
    @media (max-width: 768px) {
        font-size: 24px;
    }
`;

const CertGrid = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;

    @media (max-width: 768px) {
        flex-wrap: nowrap;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        justify-content: flex-start;
        padding: 0 20px 12px 25px;
        scrollbar-width: thin;
        scrollbar-color: ${({ theme }) => theme.primary + '60'} transparent;

        &::-webkit-scrollbar {
            height: 4px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background: ${({ theme }) => theme.primary + '60'};
            border-radius: 4px;
        }
    }
`;

const CertCard = styled.div`
    width: 100%;
    max-width: 320px;
    min-height: 180px;
    background-color: ${({ theme }) => theme.card};
    border: 0.1px solid #854CE6;
    border-radius: 16px;
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: rgba(133, 76, 230, 0.2) 0px 4px 20px;
    }

    @media (max-width: 768px) {
        scroll-snap-align: start;
        flex-shrink: 0;
        width: 80vw;
        max-width: 300px;
        min-height: 180px;
        padding: 16px 20px;

    }
`;

const CertCardTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
`;

const CertCardIssuer = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.primary};
`;

const CertCardDate = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.text_secondary};
`;

const CertCardDesc = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.text_secondary};
    margin-top: 4px;
`;

const Education = () => {
  const { t, language } = useLanguage();

  return (
    <Container id="education">
      <Wrapper>
        <Title>{t.educationTitle}</Title>
        <Desc>{t.educationDesc}</Desc>
        <TimeLineSection>
          <TimeLine>
            {education.map((item, index) => (
              <TimelineItem key={item.id}>
                <TimelineContent sx={{ px: 2, py: "12px" }}>
                  <EducationCard education={item} />
                </TimelineContent>
                <TimelineSeparator>
                  <TimelineDot variant='outlined' color="secondary" />
                  {index !== education.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
              </TimelineItem>
            ))}
          </TimeLine>
        </TimeLineSection>

        <CertSection>
          <CertTitle>{t.certifications}</CertTitle>
          <CertGrid>
            {certifications.map((cert) => (
              <CertCard key={cert.id}>
                <CertCardTitle>{typeof cert.title === 'object' ? cert.title[language] : cert.title}</CertCardTitle>
                <CertCardIssuer>{cert.issuer}</CertCardIssuer>
                <CertCardDate>{cert.date}</CertCardDate>
                <CertCardDesc>{typeof cert.desc === 'object' ? cert.desc[language] : cert.desc}</CertCardDesc>
              </CertCard>
            ))}
          </CertGrid>
        </CertSection>
      </Wrapper>
    </Container>
  )
}

export default Education
